import { MaterialShape } from '../interfaces/Material.Interface.ts';
import { Lineitem } from '../interfaces/Ordel.Interface.ts';
import { getEffectiveShapeName } from './shapeUtils';

interface IMaterialDimensions {
	material_shape: string;
	material_height?: string | null;
	material_width?: string | null;
	material_outer_diameter?: string | null;
	material_inner_diameter?: string | null;
	material_wall_thickness?: string | null;
}

const statusToColorList = [
    { status: 'Not Started', color: '' },
    { status: 'In Progress', color: 'green' },
    { status: 'Completed', color: 'blue' },
	{ status: 'On Hold', color: 'gold' },
	{ status: 'Canceled', color: 'pink' },
	{ status: 'Cancelled', color: 'volcano' },
	{ status: 'Received', color: '' },
	{ status: 'Partially On Hold', color: 'gold' },
	{ status: 'Partially Cancelled', color: 'orange' },
	{ status: 'Partially On Hold & Cancelled', color: 'orange' },
	{ status: 'Late', color: 'red' },
	{ status: 'Draft', color: '' },
    { status: 'Submitted', color: 'green' },
    { status: 'Delivered In Full', color: 'blue' },
    // Delivery related mappings
    { status: 'Partial Delivery', color: 'green' },
    { status: 'Delivered in Full', color: 'blue' },
    { status: 'Partially Delivered', color: 'green' },
    { status: 'Fully Delivered', color: 'blue' },
	{ status: 'Not Cut', color: '' },
	{ status: 'Cut', color: 'blue' },
	{ status: 'INCH', color: 'magenta' },
	{ status: 'MM', color: 'purple' },
	{ status: 'Pending', color: 'green' },
	{ status: 'Quantity Changed', color: 'orange' },
	{ status: 'Not Measured', color: '' },
	{ status: 'Yes', color: 'blue' },
	{ status: 'No', color: 'volcano' },
	{ status: 'Rejected', color: 'volcano' },
	// Material Length statuses
	{ status: 'Awaiting Delivery', color: 'yellow' },
	{ status: 'In Stock', color: 'green' },
	{ status: 'Quality Hold', color: 'orange' },
	{ status: 'Scrapped', color: 'red' },
];

export function getProductionMethodTagColor(name: string) {
	const colors = [
		{ name: 'Fastlane', color: 'lime' },
		{ name: 'QuickTurn', color: 'magenta' },
		{ name: 'Swiss', color: 'cyan' },
		{ name: 'Traditional', color: 'purple' },
	];

	const color = colors.find((x) => x.name == name);
	if (color) {
		return color.color;
	}

	return 'default';
}

export function getOrderStatusTagColor(status: string) {
	const color = statusToColorList.find((x) => x.status == status);
	if (color) {
		return color.color;
	}

	return 'default';
}

export function getTagColor(status: string) {
	const color = statusToColorList.find((x) => x.status == status);
	if (color) {
		return color.color;
	}

	return 'default';
}

export function getOrderLineItemStatusTagColor(status: string) {
	const color = statusToColorList.find((x) => x.status == status);
	if (color) {
		return color.color;
	}

	return 'default';
}

export function getOrderLineItemProductionStatusTagColor(status: string) {
	const color = statusToColorList.find((x) => x.status == status);
	if (color) {
		return color.color;
	}

	return 'default';
}

export function getTotalPartsQty(lineItems: Lineitem[]) {
	return lineItems.reduce(
		(totalQty, item) =>
			totalQty +
			(item.cancelled_quantity > 0 ? item.updated_quantity : item.quantity),
		0
	);
}

export const getShapeNameById = (
	shapes: MaterialShape[],
	id: string | undefined | null
) => {
	if (id) {
		return getEffectiveShapeName(
			shapes.find((x) => x.id === id)?.display_name
		);
	}
};

// Use this function when accessing the display_size property from Material is not possible.
export const getDisplaySize = ({
	material_shape,
	material_height,
	material_width,
	material_outer_diameter,
	material_inner_diameter,
	material_wall_thickness,
}: IMaterialDimensions): string => {
	const effectiveShape = getEffectiveShapeName(material_shape);

	if (
		(effectiveShape === 'Rectangle Bar' || effectiveShape === 'Plate') &&
		material_height != null &&
		material_width != null
	) {
		return `${material_height} H. x ${material_width} W.`;
	}

	if (effectiveShape === 'Hex Bar' && material_height != null) {
		return `${material_height} H.`;
	}

	if (effectiveShape === 'Round Bar' && material_outer_diameter != null) {
		return `${material_outer_diameter} O.D.`;
	}

	if (
		effectiveShape === 'Round Tube' &&
		material_outer_diameter != null &&
		material_inner_diameter != null
	) {
		return `${material_outer_diameter} O.D. x ${material_inner_diameter} I.D.`;
	}

	if (
		(effectiveShape === 'Rectangle Tube' ||
			effectiveShape === 'Angle' ||
			effectiveShape === 'Channel') &&
		material_height != null &&
		material_width != null &&
		material_wall_thickness != null
	) {
		return `${material_height} H. x ${material_width} W. x ${material_wall_thickness} W.T.`;
	}

	return '';
};

export const normalizeStatusDelivered = (status: string): string => {
	return status
		.replace(/Delivered/g, 'Received')
		.replace(/Delivery/g, 'Receive');
};

export const DEFAULT_SAW_KERF = 0.07;
export const SWISS_SAW_KERF = 0.090;
export const DEFAULT_TRIM_CUT_LENGTH = 0.5;

export const calculateMaterialArea = (dimensions: IMaterialDimensions): number => {
	const {
		material_shape,
		material_height,
		material_width,
		material_outer_diameter,
		material_inner_diameter,
		material_wall_thickness
	} = dimensions;

	const d = (val: string | number | null | undefined) => {
		if (val === null || val === undefined) return 0;
		return typeof val === 'string' ? parseFloat(val) : val;
	};
	const effectiveShape = getEffectiveShapeName(material_shape);

	let area = 0;

	if (effectiveShape === "Rectangle Bar" || effectiveShape === "Plate") {
		area = d(material_height) * d(material_width);
	} else if (effectiveShape === "Round Bar") {
		const radius = d(material_outer_diameter) / 2;
		area = (radius ** 2) * Math.PI;
	} else if (effectiveShape === "Round Tube") {
		const rmax = d(material_outer_diameter) / 2;
		const rmin = d(material_inner_diameter) / 2;
		area = Math.PI * ((rmax ** 2) - (rmin ** 2));
	} else if (effectiveShape === "Hex Bar") {
		const h = d(material_height);
		area = (Math.sqrt(3) / 2) * (h ** 2);
	} else if (effectiveShape === "Channel") {
		const h = d(material_height);
		const w = d(material_width);
		const t = d(material_wall_thickness);
		area = (2 * (h - t) + w) * t;
	} else if (effectiveShape === "Angle") {
		const h = d(material_height);
		const w = d(material_width);
		const t = d(material_wall_thickness);
		area = (h - t + w) * t;
	} else if (effectiveShape === "Rectangle Tube") {
		const h = d(material_height);
		const w = d(material_width);
		const t = d(material_wall_thickness);
		const outer = h * w;
		const inner = (h - 2 * t) * (w - 2 * t);
		area = outer - inner;
	}

	return Math.max(area, 0);
};